import React, { FC, useEffect, useState, useRef } from "react";
import { getNoteName } from "../helpers/midiHelpers";
import { Chord } from "tonal";
import { Midi } from "tonal";
import { OctaveOptions } from "../models/customTypes";
import { useMidi } from "../contexts/MidiContext"; // Make sure this path is correct
import { createFrameLoop } from "./frameLoop";
import type { FrameLoop } from "./frameLoop";
import {
  computeChordBubbleFrame,
  isChordBubbleExpired,
} from "./chordBubble";
import type { ChordBubble, ChordBubbleFrame } from "./chordBubble";

import "./PianoCanvas.scss";

type PianoCanvasProps = {
  videoWidth: number;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  pressedKeyColor?: string;
  numOctaves: OctaveOptions;
  octaveOffset: number;
  chordsEnabled: boolean;
  bubbleChords: boolean;
};

const PianoCanvas: FC<PianoCanvasProps> = ({
  videoWidth,
  canvasRef,
  pressedKeyColor,
  numOctaves,
  octaveOffset,
  chordsEnabled,
  bubbleChords,
}) => {
  const defaultPressedColor = pressedKeyColor || "#00FF00";

  const totalWhiteKeys = 52; // 52 white keys in an 88 key keyboard.  Although my rendered keyboard is not quite 88 keys, keeping this for now.

  const pianoYPos = 0; // Y Position of the piano on the canvas

  const [whiteKeyHeight, setWhiteKeyHeight] = useState<number>(112);

  const [whiteKeyWidth, setWhiteKeyWidth] = useState<number>(25);

  const { noteHash } = useMidi(); // Access noteHash directly from the context

  /* Which chord bubble is on screen and when it started — not how it looks
     right now. Opacity and scale are derived from the clock at paint time (see
     chordBubble.ts), so this state changes once per chord instead of once per
     frame. */
  const [chordAnimation, setChordAnimation] = useState<ChordBubble | null>(null);

  /* The single animation loop for the bubble. Created once — `useRef(create())`
     would build a new loop on every render and throw it away. */
  const bubbleLoopRef = useRef<FrameLoop | null>(null);

  const chordDebounceTime = 200; // 200ms debounce for chord detection

  // Debounce refs to avoid re-render issues
  const debounceRef = useRef<{
    detectedChord: string | null;
    firstDetectedTime: number;
    lastDetectedTime: number;
  } | null>(null);

  // State for the actual chord to display (only updated after debounce)
  const [debouncedChord, setDebouncedChord] = useState<string | null>(null);

  // Function to format chord names for better readability
  const formatChordName = (chord: string): string => {
    if (!chord) return chord;
    
    // Replace common chord symbols with more readable versions
    return chord
      .replace(/M/g, 'maj')  // Major chords - use lowercase 'maj' for readability
      .replace(/\bm\b/g, 'min')  // Minor chords - only replace standalone 'm', not 'm' in 'maj'
      .replace(/dim/g, 'dim') // Diminished (already readable)
      .replace(/aug/g, 'aug') // Augmented (already readable)
      .replace(/sus/g, 'sus') // Suspended (already readable)
      .replace(/add/g, 'add') // Added notes (already readable)
      .replace(/\d+/g, (match) => match); // Keep numbers as is
  };


  /* These mappers can be generated using helpers/midiHelpers.ts */
  const whiteNotesMapper = [
    0, 2, 4, 5, 7, 9, 11, 12, 14, 16, 17, 19, 21, 23, 24, 26, 28, 29, 31, 33,
    35, 36, 38, 40, 41, 43, 45, 47, 48, 50, 52, 53, 55, 57, 59, 60, 62, 64, 65,
    67, 69, 71, 72, 74, 76, 77, 79, 81, 83, 84, 86, 88, 89, 91, 93, 95, 96, 98,
    100, 101, 103, 105, 107, 108, 110, 112, 113, 115, 117, 119, 120, 122, 124,
    125, 127,
  ];

  const blackNotesMapper = [
    1, 3, 6, 8, 10, 13, 15, 18, 20, 22, 25, 27, 30, 32, 34, 37, 39, 42, 44, 46,
    49, 51, 54, 56, 58, 61, 63, 66, 68, 70, 73, 75, 78, 80, 82, 85, 87, 90, 92,
    94, 97, 99, 102, 104, 106, 109, 111, 114, 116, 118, 121, 123, 126,
  ];

  /* These have to assume a width */
  useEffect(() => {
    switch (numOctaves) {
      case 1:
        setWhiteKeyHeight(82);
        setWhiteKeyWidth(22);
        break;
      case 3:
        setWhiteKeyHeight(190);
        setWhiteKeyWidth(49.4);
        break;
      case 5:
        setWhiteKeyHeight(135);
        setWhiteKeyWidth(30);
        break;
      case 6:
        setWhiteKeyHeight(112);
        setWhiteKeyWidth(25.2);
        break;
    }
  }, [numOctaves]);

  const adjustNoteByOctave = (note: number): number => {
    return note + (octaveOffset - 1) * 12;
  };

  const getNoteLabel = (midiNote: number): string | null => {
    const noteIndex = midiNote % 12;
    const octave = Math.floor(midiNote / 12) - 1;

    // Only label 'C' notes which have a noteIndex of 0
    if (noteIndex === 0) {
      return `C${octave}`;
    }
    return null; // Don't label any other notes
  };

  /* Font size and position offset for the white-key labels C1, C2, C3 etc when the user changes piano size */
  const getFontSizeAndOffset = (octaves: number): [string, number] => {
    switch (octaves) {
      case 3:
        return ["20", 20];
      case 5:
        return ["16", 13];
      case 6:
        return ["12", 12];
      default:
        return ["12", 12];
    }
  };

  // Heuristic: prefer simple "Root add 11" labeling when notes match a major triad + perfect fourth
  const trySimplifyToAdd11 = (noteNames: string[]): string | null => {
    if (!noteNames || noteNames.length < 3) return null;

    // Unique pitch classes 0..11
    const pitchClasses = Array.from(
      new Set(
        noteNames
          .map(n => {
            try { return Midi.toMidi(n) ?? undefined; } catch { return undefined; }
          })
          .filter((m): m is number => typeof m === "number")
          .map(m => ((m % 12) + 12) % 12)
      )
    );

    if (pitchClasses.length < 3) return null;

    const pcToName = (pc: number): string => {
      // Use sharps to stay consistent with Midi.midiToNoteName default
      const names = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];
      return names[((pc % 12) + 12) % 12];
    };

    // For each pitch class present, test if it forms a major triad (0,4,7) and also contains 11th (5)
    for (const rootPc of pitchClasses) {
      const has = (intervalPc: number) => pitchClasses.includes(((rootPc + intervalPc) % 12 + 12) % 12);

      const isMajorTriad = has(0) && has(4) && has(7);
      const hasEleven = has(5); // perfect fourth relative to root => 11th

      // Avoid labeling as add11 if seventh or ninth present; keep simple
      const hasSeventh = has(10) || has(11);
      const hasNinth = has(2);

      if (isMajorTriad && hasEleven && !hasSeventh && !hasNinth) {
        return `${pcToName(rootPc)}add11`;
      }
    }

    return null;
  };

  // Prefer candidate chord names that include richer information (7ths/9ths) over simple add11
  const rankChordCandidates = (candidates: string[]): string | null => {
    if (!candidates || candidates.length === 0) return null;

    const score = (name: string): number => {
      const n = name.toLowerCase();
      const isSimpleMajor = /^[A-G](#|b)?(M)?$/.test(name);
      const isSimpleMinor = /^[A-G](#|b)?m$/.test(name);
      const isSlashChord = name.includes("/");
      const hasAlteredFifth = /[#b]5/.test(name);

      const hasMaj7 = /maj7|ma7|Δ7|m7\(\)/.test(n) || /maj7/.test(n);
      const has7 = hasMaj7 || /7/.test(n); // crude but favors any 7th
      const has9 = /add9|\(9\)|9(?!\d)/.test(n) || /9/.test(n);
      const isAdd11 = /add11/.test(n) && !has7 && !has9;
      let s = 0;
      if (isSimpleMajor || isSimpleMinor) s += 8;
      if (hasMaj7) s += 6; else if (has7) s += 4;
      if (has9) s += 3;
      if (isAdd11) s -= 2;
      if (isSlashChord) s -= 5;
      if (hasAlteredFifth) s -= 4;
      // Slight preference for longer, more descriptive names
      s += Math.min(2, Math.max(0, name.length - 3) / 6);
      return s;
    };

    let best = candidates[0];
    let bestScore = score(best);
    for (let i = 1; i < candidates.length; i++) {
      const sc = score(candidates[i]);
      if (sc > bestScore) {
        best = candidates[i];
        bestScore = sc;
      }
    }
    return best;
  };

  const drawWhiteKey = (
    ctx: CanvasRenderingContext2D,
    i: number,
    yOffset: number,
    whiteKeyWidth: number,
    whiteKeyHeight: number,
    fontSize: string,
    offset: number,
    noteHash: Record<number, boolean>,
    cornerRadius: number // Added corner radius parameter
  ) => {
    const x = i * whiteKeyWidth;
    const midiNote = adjustNoteByOctave(whiteNotesMapper[i]);

    // Define the bottom corners with some radius
    const bottomLeftCornerX = x;
    const bottomLeftCornerY = yOffset + whiteKeyHeight - cornerRadius;

    const bottomRightCornerX = x + whiteKeyWidth;
    const bottomRightCornerY = yOffset + whiteKeyHeight - cornerRadius;

    // Start the path for the white key shape
    ctx.beginPath();
    ctx.moveTo(x, yOffset); // Move to the top-left corner
    ctx.lineTo(x + whiteKeyWidth, yOffset); // Draw line to the top-right corner
    ctx.lineTo(x + whiteKeyWidth, bottomRightCornerY); // Draw line to the start of bottom-right corner curve

    // Create the rounded corner on the bottom-right
    ctx.arcTo(
      bottomRightCornerX,
      yOffset + whiteKeyHeight,
      bottomRightCornerX - cornerRadius,
      yOffset + whiteKeyHeight,
      cornerRadius
    );

    // Draw line to the start of bottom-left corner curve
    ctx.lineTo(bottomLeftCornerX + cornerRadius, yOffset + whiteKeyHeight);

    // Create the rounded corner on the bottom-left
    ctx.arcTo(
      bottomLeftCornerX,
      yOffset + whiteKeyHeight,
      bottomLeftCornerX,
      bottomLeftCornerY,
      cornerRadius
    );

    // Draw line up to the top-left corner to close the path
    ctx.lineTo(x, yOffset);

    ctx.closePath(); // Close the path back to the top-left corner

    // Fill the key
    ctx.fillStyle = noteHash[midiNote]
      ? pressedKeyColor || defaultPressedColor
      : "#F5F2E4";
    ctx.fill();

    // Stroke the key border
    ctx.strokeStyle = "black";
    ctx.stroke();

    // Draw the key label
    const label = getNoteLabel(midiNote);
    if (label) {
      ctx.fillStyle = "#787276";
      ctx.font = `${fontSize}px Tahoma`;
      const labelWidth = ctx.measureText(label).width;
      ctx.fillText(
        label,
        x + (whiteKeyWidth - labelWidth) / 2,
        yOffset + whiteKeyHeight - offset
      );
    }
  };

  // Black keys do not sit perfect in the center of white keys. We need to calculate the offset which depends on the black note.
  const getBlackKeyOffsets = (midiNote: number) => {
    const noteName = getNoteName(midiNote);

    switch (noteName) {
      case "C#":
        return -2.2;
      case "D#":
        return 2.2;
      case "F#":
        return -2.2;
      case "A#":
        return 2.2;
      default:
        return 0;
    }
  };

  const drawBlackKey = (
    ctx: CanvasRenderingContext2D,
    i: number,
    yOffset: number,
    whiteKeyWidth: number,
    blackKeyWidth: number,
    blackKeyHeight: number,
    midiNote: number,
    noteHash: Record<number, boolean>,
    cornerRadius: number // Added corner radius parameter
  ) => {
    const offset = getBlackKeyOffsets(midiNote);

    // Calculate the position of the black key
    const x = (i + 1) * whiteKeyWidth - blackKeyWidth / 2 + offset;

    // Define the bottom corners with some radius
    const bottomLeftCornerX = x;
    const bottomLeftCornerY = yOffset + blackKeyHeight - cornerRadius;

    const bottomRightCornerX = x + blackKeyWidth;
    const bottomRightCornerY = yOffset + blackKeyHeight - cornerRadius;

    // Begin the path for the black key shape
    ctx.beginPath();
    ctx.moveTo(x, yOffset); // Move to the top-left corner of the black key
    ctx.lineTo(x + blackKeyWidth, yOffset); // Draw line to the top-right corner
    ctx.lineTo(x + blackKeyWidth, bottomRightCornerY); // Draw line to the start of bottom-right corner curve

    // Create the rounded corner on the bottom-right
    ctx.arcTo(
      bottomRightCornerX,
      yOffset + blackKeyHeight,
      bottomRightCornerX - cornerRadius,
      yOffset + blackKeyHeight,
      cornerRadius
    );

    // Draw line to the start of bottom-left corner curve
    ctx.lineTo(bottomLeftCornerX + cornerRadius, yOffset + blackKeyHeight);

    // Create the rounded corner on the bottom-left
    ctx.arcTo(
      bottomLeftCornerX,
      yOffset + blackKeyHeight,
      bottomLeftCornerX,
      bottomLeftCornerY,
      cornerRadius
    );

    // Draw line up to the top-left corner to close the path
    ctx.lineTo(x, yOffset);

    ctx.closePath();

    // Fill the key with black color as the base
    ctx.fillStyle = noteHash[midiNote]
      ? pressedKeyColor || defaultPressedColor
      : "#202020";
    ctx.fill();

    // Create the glossy gradient effect
    const gradient = ctx.createLinearGradient(
      x,
      yOffset,
      x,
      yOffset + blackKeyHeight / 2
    );
    gradient.addColorStop(0, "rgba(255, 255, 255, 0.4)"); // Slight gloss at the top
    gradient.addColorStop(1, "rgba(0, 0, 0, 0)"); // Transition to fully transparent

    // Apply the glossy gradient on top of the base color
    ctx.fillStyle = gradient;
    ctx.fill();

    // Optionally draw a border around the key
    ctx.strokeStyle = "black";
    ctx.stroke();
  };

  const drawPiano = () => {
    if (canvasRef.current) {
      canvasRef.current.width = 1080; // Set the drawing buffer size
      canvasRef.current.height = 1350; // Set the drawing buffer size
      canvasRef.current.style.width = videoWidth + "px"; // Display size width
      canvasRef.current.style.height = "675px"; // Display size height

      const ctx = canvasRef.current.getContext("2d")!;

      const blackKeyWidth = whiteKeyWidth / 2;
      const blackKeyHeight = whiteKeyHeight * 0.6;
      const [fontSize, offset] = getFontSizeAndOffset(numOctaves);

      // Draw white keys
      for (let i = 0; i < totalWhiteKeys; i++) {
        drawWhiteKey(
          ctx,
          i,
          pianoYPos,
          whiteKeyWidth,
          whiteKeyHeight,
          fontSize,
          offset,
          noteHash,
          2.5
        );
      }

      // Adjust black notes once before drawing
      const adjustedBlackNotesMapper = blackNotesMapper.map(adjustNoteByOctave);

      // Draw black keys
      const blackKeyPattern = [1, 1, 0, 1, 1, 1, 0];
      let blackNoteCounter = 0;
      for (
        let i = 0;
        i < totalWhiteKeys &&
        blackNoteCounter < adjustedBlackNotesMapper.length;
        i++
      ) {
        if (blackKeyPattern[i % 7]) {
          drawBlackKey(
            ctx,
            i,
            pianoYPos,
            whiteKeyWidth,
            blackKeyWidth,
            blackKeyHeight,
            adjustedBlackNotesMapper[blackNoteCounter],
            noteHash,
            1.5
          );
          blackNoteCounter++;
        }
      }
    }
  };

  function drawRegularChordText(currentChord: string, ctx: CanvasRenderingContext2D) {
    // Text properties
    const text = formatChordName(currentChord);
    const textSize = 45;
    ctx.font = `bold ${textSize}px Times`;

    // Measure text
    const textMetrics = ctx.measureText(text);
    const textWidth = textMetrics.width;
    const padding = 17; // Padding around the text

    // Rectangle dimensions and position
    const rectX = 500 - padding / 2; // Adjust based on padding
    const rectY = 195 - textSize - padding / 2; // Adjust based on text size and padding
    const rectWidth = textWidth + padding;
    const rectHeight = textSize + padding;

    let chordPadding = 0;
    switch (numOctaves) {
      case 3: {
        chordPadding += 70;
        break;
      }
      case 5: {
        chordPadding += 50;
        break;
      }
      case 6: {
        chordPadding += 25;
        break;
      }
      default:
        break;
    }

    // Draw rectangle
    ctx.fillStyle = "#8f8f8f"; // Rectangle color (can be different from text color)
    ctx.fillRect(rectX, rectY + chordPadding, rectWidth, rectHeight + 10);

    // Draw text on top
    ctx.fillStyle = "white"; // Text color
    ctx.fillText(text, 500, rectY + 50 + chordPadding);
  }

  function drawBubbleChordText(
    currentChord: string,
    ctx: CanvasRenderingContext2D,
    frame: ChordBubbleFrame,
  ) {
    const text = formatChordName(currentChord);
    const baseTextSize = 60; // Increased from 50 to 60 (20% bigger)
    const textSize = baseTextSize * frame.scale;

    // Use a more bubbly font
    ctx.font = `bold ${textSize}px "Comic Sans MS", cursive, sans-serif`;
    
    // Calculate position with animation
    const centerX = 500;
    const centerY = 195;
    
    let chordPadding = 0;
    switch (numOctaves) {
      case 3: {
        chordPadding += 70;
        break;
      }
      case 5: {
        chordPadding += 50;
        break;
      }
      case 6: {
        chordPadding += 25;
        break;
      }
      default:
        break;
    }

    // No background - just text with animation

    // Add shadow effect for better visibility
    ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
    ctx.shadowBlur = 8 * frame.scale;
    ctx.shadowOffsetX = 3 * frame.scale;
    ctx.shadowOffsetY = 3 * frame.scale;

    // Draw text with opacity
    ctx.globalAlpha = frame.opacity;
    ctx.fillStyle = "#ffffff"; // White text for better visibility
    ctx.textAlign = "center";
    ctx.fillText(text, centerX, centerY + chordPadding);
    
    // Reset shadow
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.textAlign = "left";
    ctx.globalAlpha = 1;
  }

  function getCurrentNotesPressed() {
    return Object.keys(noteHash)
      .filter((key) => noteHash[parseInt(key, 10)] === true)
      .map((key) => parseInt(key, 10));
  }

  useEffect(() => {
    drawPiano();
  }, [noteHash, whiteKeyHeight, whiteKeyWidth, numOctaves, octaveOffset]);

  /* Debounce effect - runs every 50ms to check debounce timing */
  useEffect(() => {
    if (!chordsEnabled) {
      setDebouncedChord(null);
      debounceRef.current = null;
      return;
    }

    const interval = setInterval(() => {
      const now = Date.now();
      
      if (debounceRef.current) {
        const timeSinceFirstDetected = now - debounceRef.current.firstDetectedTime;
        
        // If debounce period has passed and we don't have this chord displayed yet, set it
        if (timeSinceFirstDetected >= chordDebounceTime && debouncedChord !== debounceRef.current.detectedChord) {
          setDebouncedChord(debounceRef.current.detectedChord);
        }
      } else {
        setDebouncedChord(null);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [chordsEnabled, chordDebounceTime, debouncedChord]);

  /* ── Bubble chord animation ───────────────────────────────────────────────
     One long-lived loop, started while a bubble is on screen and stopped by
     the effect's cleanup. The painter is republished on every render and read
     through a ref, so the loop always paints the current piano without having
     to be torn down and rebuilt whenever a key is pressed. */

  const drawBubbleFrameRef = useRef<() => void>(() => {});

  if (bubbleLoopRef.current === null) {
    bubbleLoopRef.current = createFrameLoop({
      onFrame: () => drawBubbleFrameRef.current(),
      onError: (error, consecutiveErrors) => {
        // Noisy on the first failure only; a stuck canvas would otherwise
        // flood the console at 60 Hz.
        if (consecutiveErrors === 1) {
          console.warn("Chord bubble frame failed:", error);
        }
      },
    });
  }

  const drawBubbleFrame = () => {
    const canvas = canvasRef.current;
    if (!canvas || !chordAnimation) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const now = Date.now();
    if (isChordBubbleExpired(chordAnimation, now)) {
      // Repaint without the bubble so the faded-out text cannot linger, then
      // drop the state — which stops this loop via the effect below.
      drawPiano();
      setChordAnimation(null);
      return;
    }

    // drawPiano resets the drawing buffer, which clears the previous frame.
    drawPiano();
    drawBubbleChordText(
      chordAnimation.chord,
      ctx,
      computeChordBubbleFrame(chordAnimation, now),
    );
  };

  /* Point the loop at this render's painter. No dependency list on purpose:
     the painter closes over current state, and a loop outliving a render must
     never keep calling a stale one. */
  useEffect(() => {
    drawBubbleFrameRef.current = drawBubbleFrame;
  });

  const bubbleActive = bubbleChords && chordAnimation !== null;

  useEffect(() => {
    const loop = bubbleLoopRef.current;
    if (!loop || !bubbleActive) return;

    loop.start();
    return () => loop.stop();
  }, [bubbleActive]);

  /* Belt and braces: never leave a loop running past unmount. */
  useEffect(() => {
    const loop = bubbleLoopRef.current;
    return () => loop?.stop();
  }, []);

  /* This effect is for drawing chord text on screen */
  useEffect(() => {
    if (!chordsEnabled) {
      // Clear animation if chords are disabled
      if (bubbleChords) {
        setChordAnimation(null);
      }
      return;
    }

    const currentMidiNotesPressed = getCurrentNotesPressed();
    const now = Date.now();

    // If no keys are pressed at all, release and clear
    if (!currentMidiNotesPressed || currentMidiNotesPressed.length === 0) {
      if (debouncedChord) {
        setDebouncedChord(null);
        debounceRef.current = null;
      }
      if (bubbleChords && chordAnimation && !chordAnimation.isReleased) {
        setChordAnimation(prev => prev ? {
          ...prev,
          isReleased: true,
          releaseTime: now
        } : null);
      }
      return;
    }

    const currentNotesNamesPressed = currentMidiNotesPressed.map((midiNum) =>
      Midi.midiToNoteName(midiNum)
    );

    const currentChord = Chord.detect(currentNotesNamesPressed, {
      assumePerfectFifth: true,
    });

    // If not a valid chord, do not start new animation; keep existing bubble until all notes released
    if (!canvasRef.current || !currentChord.length || currentChord[0] === "") {
      // Do not clear existing animation unless no keys (handled above)
      // Reset debounce tracking so we don't retrigger until a valid chord is held
      debounceRef.current = null;
      return;
    }

    // Choose the best candidate first (favor maj7/9 etc.)
    let newChord = rankChordCandidates(currentChord) || currentChord[0];

    // Only simplify to add11 if there is no 7th or 9th present
    const lc = (newChord || "").toLowerCase();
    const has7or9 = /maj7|ma7|Δ7|\b7\b|add9|\(9\)|\b9\b/.test(lc);
    if (!has7or9) {
      const simplified = trySimplifyToAdd11(currentNotesNamesPressed);
      if (simplified) {
        newChord = simplified;
      }
    }

    // Update debounce ref
    if (!debounceRef.current || debounceRef.current.detectedChord !== newChord) {
      // New chord detected, start debounce
      debounceRef.current = {
        detectedChord: newChord,
        firstDetectedTime: now,
        lastDetectedTime: now
      };
    } else {
      // Same chord, update last detected time
      debounceRef.current.lastDetectedTime = now;
    }
  }, [noteHash, bubbleChords, chordsEnabled, chordAnimation]);

  /* Effect to handle debounced chord changes */
  useEffect(() => {
    const now = Date.now();

    if (bubbleChords) {
      if (debouncedChord) {
        // Restart the animation for a first chord, a different chord, or the
        // same chord struck again after being released. If it is the same
        // chord and still held, leave it alone so it stays alive.
        const needsRestart =
          !chordAnimation ||
          chordAnimation.chord !== debouncedChord ||
          chordAnimation.isReleased;

        if (needsRestart) {
          setChordAnimation({
            chord: debouncedChord,
            startTime: now,
            isReleased: false,
            releaseTime: 0,
          });
        }
      } else {
        // No debounced chord, mark current animation as released
        if (chordAnimation && !chordAnimation.isReleased) {
          setChordAnimation(prev => prev ? {
            ...prev,
            isReleased: true,
            releaseTime: now
          } : null);
        }
      }
    } else {
      // Regular chord mode - draw immediately
      if (debouncedChord && canvasRef.current) {
        const ctx = canvasRef.current.getContext("2d")!;
        drawRegularChordText(debouncedChord, ctx);
      }
    }
  }, [debouncedChord, bubbleChords, chordAnimation]);

  /* Bubble chords are painted by the frame loop above, not by an effect. */

  return (
    <div>
      <canvas
        className="piano-canvas"
        ref={canvasRef}
        width={videoWidth}
        height={100}
      ></canvas>
    </div>
  );
};

export default PianoCanvas;
